import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import { getInstAccountsReactionsData } from "../../../services/dashboard";
import {
  getMonthlyInstitutionalAccReactions,
  getYearlyInstitutionalAccountsReactions,
} from "../../../services/singleMonths";
import { getDateString } from "../../../utils/functions/getDateString";
import { getMonthText } from "../../../utils/functions/getMonthText";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { getSentimentLineGraphOptions } from "../../../utils/constants/graphConfigs";

import { reactionMigAccounts as texts_es } from "../../../utils/text/graphTexts";
import { reactionMigAccounts as texts_en } from "../../../utils/text/graphTexts_en";
import CenterSpin from "../../misc/spin";
import CardContainer from "../../reusable/CardContainer";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function ReactionsToInstiturions({ selectedCountry }) {
  const { country } = useParams();
  const { dateOptions, errorText } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("reactions");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  // Unique to Tweets component
  // Never send unique states to Card components

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
    }
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);
        setOptions();

        const response = await getInstAccountsReactionsData(
          currentCountry,
          selectedYear
        );

        const monthsRealNames = response.data.dates.map((date) =>
          getMonthText(date, language, false)
        );

        if (response) {
          const options = getSentimentLineGraphOptions(
            response,
            monthsRealNames,
            language
          );

          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setOptions(null);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, selectedYear, isLoadingInitialData, currentCountry]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      let promises, callback;
      if (isMostRecentYear) {
        const lastMonthDataResponse = getMonthlyInstitutionalAccReactions(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        promises = [lastMonthDataResponse];
        callback = (results) => {
          if (results[0].data.positive_percentage === 0) setLastMonthData("0%");
          else
            setLastMonthData(
              results[0].data.positive_percentage.toFixed(1) + "%"
            );
        };
      } else {
        const currentYearPromise = getYearlyInstitutionalAccountsReactions(
          currentCountry,
          selectedYear
        );

        promises = [currentYearPromise];
        callback = (results) => {
          setLastMonthData(results[0].data.value.toFixed(1) + "%");
        };
      }
      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setLastMonthData("--");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function InstitutionsContent() {
    let content;
    try {
      if (dateOptions === errorText) throw Error;
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle(isMostRecentYear)}
          description={texts.description}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es"
              ? "ReaccionesAInstituciones"
              : "ReactionToInstitutions",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle()}
          description={texts.description}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <InstitutionsContent />}
    </CardContainer>
  );
}
